import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth } from '../utils/firebase-config';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import './labcss/backup-settings.css';
import { SUPPORTED_CURRENCIES, getCurrencySymbol } from '../utils/currency';

export default function SettingsPage() {
  const { 
    user, 
    userProfile, 
    resetPassword, 
    updateCurrencyPreference, 
    currencyCode,
    linkGoogleAccount,
  } = useAuth();

  // People who joined with a password can add Google as a second way into the same account.
  const hasGoogle = user?.providerData?.some(p => p.providerId === 'google.com');
  const [googleMessage, setGoogleMessage] = useState(null);
  const handleConnectGoogle = async () => {
    setGoogleMessage(null);
    try {
      await linkGoogleAccount();
      setGoogleMessage({ type: 'success', text: 'Google is connected. You can now sign in with Google or your password.' });
    } catch (err) {
      if (['auth/popup-closed-by-user', 'auth/cancelled-popup-request'].includes(err.code)) return;
      setGoogleMessage({ type: 'error', text: err.code === 'auth/credential-already-in-use'
        ? 'That Google account already belongs to another SoapLab account.'
        : err.code === 'auth/popup-blocked' ? 'Your browser blocked the Google window. Allow pop-ups for soaplab.in and try again.'
        : 'Google could not be connected. Please try again.' });
    }
  };

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState(null);
  const [passwordError, setPasswordError] = useState(null);

  // Reset email state
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // Currency preference state
  const [currencyLoading, setCurrencyLoading] = useState(false);
  const [currencyMsg, setCurrencyMsg] = useState(null);

  const isGoogleOnly = user?.providerData?.every(p => p.providerId === 'google.com');

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMsg(null);
    setPasswordError(null);

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    setPasswordLoading(true);
    try {
      // Re-authenticate first
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);

      // Update password
      await updatePassword(auth.currentUser, newPassword);
      setPasswordMsg('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setPasswordError('Current password is incorrect.');
      } else if (err.code === 'auth/requires-recent-login') {
        setPasswordError('Please log out and log back in, then try again.');
      } else {
        setPasswordError(err.message || 'Failed to update password.');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSendResetEmail = async () => {
    setResetLoading(true);
    try {
      await resetPassword(user.email);
      setResetSent(true);
    } catch (err) {
      setPasswordError('Failed to send reset email. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleCurrencyChange = async (e) => {
    const nextCode = e.target.value;
    setCurrencyMsg(null);
    setCurrencyLoading(true);
    try {
      await updateCurrencyPreference(nextCode);
      setCurrencyMsg(`Currency set to ${getCurrencySymbol(nextCode)} (${nextCode})`);
    } catch (err) {
      setCurrencyMsg('Failed to update currency. Please try again.');
    } finally {
      setCurrencyLoading(false);
    }
  };

  return (
    <div className="sl-page auth-page">
      <div className="pageheader borderleft">
        <Breadcrumbs
          items={[
            { label: 'Home', to: '/' },
            { label: 'Settings' },
          ]}
        />
        <div className="pageheading">Account Settings</div>
      </div>

      <div className="settings-inner borderleft">

        {/* ── Account Info ── */}
        <div className="settings-section">
          <h3 className="settings-section-title">Account Info</h3>
          <div className="settings-field">
            <label>Email</label>
            <div className="settings-value">{user?.email || '—'}</div>
          </div>
          <div className="settings-field">
            <label>Auth Provider</label>
            <div className="settings-value">
              {user?.providerData?.map(p => {
                if (p.providerId === 'google.com') return 'Google';
                if (p.providerId === 'password') return 'Email/Password';
                return p.providerId;
              }).join(', ') || '—'}
              {!hasGoogle && (
                <button type="button" className="settings-connect-google" onClick={handleConnectGoogle}>Connect Google</button>
              )}
            </div>
            {googleMessage && <p className={`settings-google-message ${googleMessage.type}`} role="status">{googleMessage.text}</p>}
          </div>
          <div className="settings-field">
            <label>Plan</label>
            <div className="settings-value settings-plan-value">
              {userProfile?.plan || 'Free'}
              
            </div>
          </div>
        </div>

        {/* ── Preferences ── */}
        <div className="settings-section">
          <h3 className="settings-section-title">Preferences</h3>
          <div className="settings-field">
            <label>Currency</label>
            <select
              value={currencyCode || 'INR'}
              onChange={handleCurrencyChange}
              disabled={currencyLoading}
            >
              {SUPPORTED_CURRENCIES.map(code => (
                <option key={code} value={code}>
                  {getCurrencySymbol(code)} ({code})
                </option>
              ))}
            </select>
            <div className="settings-hint">
              This controls prices shown across the app (dashboards, reports, labels).
            </div>
            {currencyMsg && <div className="settings-success">{currencyMsg}</div>}
          </div>
        </div>

        {/* ── Change Password ── */}
        <div className="settings-section">
          <h3 className="settings-section-title">Change Password</h3>

          {isGoogleOnly ? (
            <div className="settings-info-box">
              <p>You signed in with Google, so there's no password to change. Your account is secured through Google.</p>
            </div>
          ) : (
            <>
              <form onSubmit={handlePasswordChange} className="settings-form">
                <div className="settings-field">
                  <label htmlFor="current-pass">Current Password</label>
                  <input
                    id="current-pass"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                </div>
                <div className="settings-field">
                  <label htmlFor="new-pass">New Password</label>
                  <input
                    id="new-pass"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    autoComplete="new-password"
                  />
                </div>
                <div className="settings-field">
                  <label htmlFor="confirm-pass">Confirm New Password</label>
                  <input
                    id="confirm-pass"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    autoComplete="new-password"
                  />
                </div>

                {passwordError && <div className="settings-error">{passwordError}</div>}
                {passwordMsg && <div className="settings-success">{passwordMsg}</div>}

                <button type="submit" className="auth-submit-btn" disabled={passwordLoading}>
                  {passwordLoading ? 'Updating...' : 'Update Password'}
                </button>
              </form>

              <div className="settings-reset-divider">
                <p className="settings-reset-hint">
                  Forgot your current password?
                </p>
                {resetSent ? (
                  <div className="settings-success">Reset email sent to {user?.email}. Check your inbox.</div>
                ) : (
                  <button
                    type="button"
                    className="auth-submit-btn sl-secondary-btn"
                    onClick={handleSendResetEmail}
                    disabled={resetLoading}
                  >
                    {resetLoading ? 'Sending...' : 'Send Password Reset Email'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
